import { crearElectores } from "../Api/ApiMetodo";
import {
  cargarFotoEscrutinio,
  cerrarEscrutinio,
  getEscrutinioMesa,
  guardarEscrutinio,
} from "../Api/ApiEscrutinio";
import { eliminarPendiente, guardarPendiente, listarPendientes } from "./almacenLocal";
import { obtenerMensajeError } from "./mensajeError";
import { obtenerUsername } from "./sesion";

// Cola de registros hechos sin internet. Cada pendiente se guarda en
// IndexedDB y se envía al servidor cuando vuelve la conexión.
//
// Tipos:
// - elector:         { datos }               -> POST /electores
// - escrutinio:      { datos, cierre? }      -> POST /escrutinio (+ PUT /cerrar)
//                    uno por mesa (clave corporación|lugar|mesa): lo último reemplaza
// - fotoEscrutinio:  { id, foto }            -> PUT /escrutinio/foto (acta ya cerrada)

export const TIPOS_PENDIENTE = {
  elector: "Registro de elector",
  escrutinio: "Escrutinio de mesa",
  fotoEscrutinio: "Foto del E-14",
};

export const EVENTO_SINCRONIZACION = "sincronizacion-estado";

// Sin respuesta del servidor = sin conexión (red caída o tiempo agotado)
export const esErrorDeRed = (error) => Boolean(error) && !error.response && error.code !== "ERR_CANCELED";

export const claveEscrutinio = ({ corporacion, lugar, mesa }) => `${corporacion}|${lugar}|${mesa}`;

// solo se envían los pendientes del usuario con sesión abierta (si otro
// usuario entra en el mismo equipo, no se mandan registros ajenos con su sesión)
export const listarPendientesUsuario = async () => {
  const usuario = obtenerUsername();
  return (await listarPendientes()).filter((pendiente) => pendiente.usuario === usuario);
};

export const buscarPendiente = async (tipo, clave) =>
  (await listarPendientesUsuario()).find((pendiente) => pendiente.tipo === tipo && pendiente.clave === clave) ?? null;

// agrega un pendiente; con clave, reemplaza al anterior del mismo tipo y clave
export const encolar = async (tipo, contenido, clave = null) => {
  const anterior = clave ? await buscarPendiente(tipo, clave) : null;
  const pendiente = {
    id: anterior?.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    tipo,
    clave,
    usuario: obtenerUsername(),
    creadoEn: anterior?.creadoEn ?? Date.now(),
    actualizadoEn: Date.now(),
    estado: "pendiente",
    error: null,
    ...contenido,
  };
  await guardarPendiente(pendiente);
  return pendiente;
};

// ─── envío de cada tipo ───
const enviarEscrutinio = async ({ datos, cierre }) => {
  let escrutinio;
  try {
    ({ escrutinio } = await guardarEscrutinio(datos));
  } catch (error) {
    // 409: ya estaba cerrado. Si este pendiente también lo cerraba, puede que
    // el cierre sí llegara antes y solo se perdiera la respuesta
    if (error.response?.status === 409 && cierre) {
      const actual = await getEscrutinioMesa(datos.corporacion, datos.lugar, datos.mesa);
      if (actual.escrutinio?.estado === "cerrado") return;
    }
    throw error;
  }
  if (cierre) {
    await cerrarEscrutinio(escrutinio.id, cierre);
  }
};

const ENVIOS = {
  elector: ({ datos }) => crearElectores(datos),
  escrutinio: enviarEscrutinio,
  fotoEscrutinio: ({ id, foto }) => cargarFotoEscrutinio(id, foto),
};

// ─── sincronización ───
let sincronizacionEnCurso = null;

const avisarEstado = (detalle) =>
  window.dispatchEvent(new CustomEvent(EVENTO_SINCRONIZACION, { detail: detalle }));

// Envía en orden los pendientes del usuario.
// - éxito: se elimina de la cola
// - sin conexión: se detiene y se reintenta luego
// - sesión vencida (401): se detiene; hay que iniciar sesión de nuevo
// - rechazo del servidor (4xx): queda "con error" para que el usuario lo revise
// - error del servidor (5xx): queda pendiente y se reintenta luego
// incluirErrores: true reintenta también los que quedaron con error
export const sincronizarPendientes = ({ incluirErrores = false } = {}) => {
  if (sincronizacionEnCurso) return sincronizacionEnCurso;

  sincronizacionEnCurso = (async () => {
    const resumen = { enviados: 0, conError: 0, sinConexion: false, sesionVencida: false };
    const pendientes = (await listarPendientesUsuario()).filter(
      (pendiente) => incluirErrores || pendiente.estado !== "error"
    );
    if (pendientes.length === 0) return resumen;

    avisarEstado({ sincronizando: true });
    for (const pendiente of pendientes) {
      try {
        await ENVIOS[pendiente.tipo](pendiente);
        await eliminarPendiente(pendiente.id);
        resumen.enviados += 1;
      } catch (error) {
        if (esErrorDeRed(error)) {
          resumen.sinConexion = true;
          break;
        }
        const estado = error.response.status;
        if (estado === 401) {
          resumen.sesionVencida = true;
          break;
        }
        if (estado >= 400 && estado < 500) {
          await guardarPendiente({ ...pendiente, estado: "error", error: obtenerMensajeError(error) });
          resumen.conError += 1;
        }
      }
    }
    return resumen;
  })().finally(() => {
    sincronizacionEnCurso = null;
    avisarEstado({ sincronizando: false });
  });

  return sincronizacionEnCurso;
};

export const estaSincronizando = () => Boolean(sincronizacionEnCurso);
